import React, { useEffect } from 'react'
import { Row, Form, Button } from 'antd'
import { useTranslation } from 'next-i18next'
import { showNotification } from 'src/core/helpers'
import styles from './ProjectFormLocation.module.scss'
import { useAppDispatch, useAppSelector, useWindowSize } from 'src/app/hooks'
import { setProjectFormItemValue, projectFormStateSelector } from 'src/features'
import ProjectFormButtonContinue from '../ProjectFormButtonContinue/ProjectFormButtonContinue'
import ProjectFormGoogleMapLocation from './ProjectFormGoogleMapLocation/ProjectFormGoogleMapLocation'
import { CustomInputDecimal, CountrySelector, FormLabels, CustomText, CustomCol } from 'src/components'

interface LocationFormPayload { latitude: number, longitude: number, country: number }

const ProjectFormLocation: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [form] = Form.useForm()
  const formState = useAppSelector(projectFormStateSelector)
  const { is_active, latitude, longitude, country } = formState
  const { width } = useWindowSize()
  const isMobile = width < 800

  const onFinish = (values: LocationFormPayload) => {
    dispatchLocationValues(values)
    dispatch(setProjectFormItemValue({ key: 'activeStep', value: 'CONTENT' }))
  }

  useEffect(() => {
    form.setFieldsValue(
      {
        ['latitude']: latitude,
        ['longitude']: longitude,
        ['country']: country ? +country : null
      })
  }, [form, latitude, longitude, country])

  const handleResetLocation = () => {
    form.resetFields()
    dispatch(setProjectFormItemValue({ key: 'latitude', value: null }))
    dispatch(setProjectFormItemValue({ key: 'longitude', value: null }))
    dispatch(setProjectFormItemValue({ key: 'country', value: null }))
    dispatch(setProjectFormItemValue({ key: 'locationText', value: null }))
    showNotification('info', t('Projects.LocationReseted'))
  }


  const dispatchLocationValues = (values: LocationFormPayload) => {
    dispatch(setProjectFormItemValue({ key: 'latitude', value: `${values.latitude}` }))
    dispatch(setProjectFormItemValue({ key: 'longitude', value: `${values.longitude}` }))
    dispatch(setProjectFormItemValue({ key: 'country', value: +values.country }))
  }

  return (
    <Form
      onFinish={onFinish}
      form={form}
    >
      <Row>
        <CustomCol>
          <FormLabels
            title={t('Projects.Location')}
            subtitle={t('Projects.LocationLabel')}
          />
        </CustomCol>
        <CustomCol>
          {/* Form Google Maps */}
          <ProjectFormGoogleMapLocation />

          {/* Form Content Here */}
          <Row className={styles.formRow}>
            <CustomCol lg={11} className='mr-4'>

              {/* Latitude Here... */}
              <CustomText label={t('Projects.Latitude')} type='secondary' />
              <CustomInputDecimal
                name='latitude'
                placeholder={t('Projects.LatitudePlaceholder')}
                onChange={(value) => dispatch(setProjectFormItemValue({ key: 'latitude', value: `${value}` }))}
                allowAllDecimals
                required={is_active}
              />

            </CustomCol>
            <CustomCol lg={isMobile ? 12 : 11} className={!isMobile && 'ml-4'}>

              {/* Longitude Here... */}
              <CustomText label={t('Projects.Longitude')} type='secondary' />
              <CustomInputDecimal
                name='longitude'
                placeholder={t('Projects.LongitudePlaceholder')}
                onChange={(value) => dispatch(setProjectFormItemValue({ key: 'longitude', value: `${value}` }))}
                allowAllDecimals
                required={is_active}
              />
            </CustomCol>
          </Row>

          <Row className={styles.formRow}>
            <CustomCol lg={isMobile ? 12 : 11} className={!isMobile && 'mr-4'}>

              {/* Country Select Here */}
              <CustomText type='secondary' label={t('settings.Country')} />
              <CountrySelector
                name='country'
                placeholder={t('admin.Brokerage.CountryPlaceHolder')}
                required={is_active}
                handleOnChange={(value) => {
                  dispatch(setProjectFormItemValue({ key: 'country', value: `${value}` }))
                }}
              />
            </CustomCol>

            <CustomCol lg={11} className='centered-div'>
              <Button
                type='text'
                size={'large'}
                className={styles.resetButton}
                onClick={handleResetLocation}
              >
                {t('Projects.ResetLocation')}
              </Button>
            </CustomCol>
          </Row>
        </CustomCol>
      </Row>

      <ProjectFormButtonContinue />
    </Form>
  )
}

export default ProjectFormLocation
